import { useMemo } from 'react';
import { applyFilters, type FilterConfig, type FilterState, TableFilters } from '@/components/filter-bar';
import type { TermsRow } from '@/services/api/terms';

export interface TermsFilterState {
  pid: string;
  email: string;
  earlyAdopter: boolean[];
  versions: string[];
}

export const EMPTY_TERMS_FILTERS: TermsFilterState = { pid: '', email: '', earlyAdopter: [], versions: [] };

// TableFilters/applyFilters need a rows array for multi-choice option derivation, but both
// multi-choice columns below always supply static `options`, so this is never read.
const NO_ROWS: TermsRow[] = [];

// earlyAdopter's boolean cells are stringified to 'Yes'/'No' so they fit the shared
// FilterState's string[]-only shape and the dialog's checkbox labels stay legible — matching
// is still exact-equality membership either way.
const EARLY_ADOPTER_OPTIONS = ['Yes', 'No'];

/**
 * Filter semantics from the legacy shared config table:
 * `text` = case-insensitive substring match (matchMode 'substring' — pid/email);
 * `multi-choice` = cell value in the selection, with an empty selection meaning
 * "filter inactive" (earlyAdopter/versions).
 */
const TERMS_FILTER_CONFIGS: Array<FilterConfig<TermsRow>> = [
  { key: 'pid', title: 'PID', type: 'multi-text', matchMode: 'substring', getValue: (row) => row.pid },
  { key: 'email', title: 'Email', type: 'multi-text', matchMode: 'substring', getValue: (row) => row.email },
  { key: 'earlyAdopter', title: 'Early Adopter', type: 'multi-choice', getValue: (row) => (row.early_adopter ? 'Yes' : 'No'), options: EARLY_ADOPTER_OPTIONS },
  // versions has no static options here — TermsFilterBar below overrides them per-render with
  // the caller-supplied versionOptions (distinct data values); applyTermsFilters
  // doesn't need real options since matching never consults FilterConfig.options.
  { key: 'versions', title: 'Terms Version', type: 'multi-choice', getValue: (row) => row.version },
];

const toSharedState = (filters: TermsFilterState): FilterState => ({
  ...(filters.pid.trim() ? { pid: [filters.pid] } : {}),
  ...(filters.email.trim() ? { email: [filters.email] } : {}),
  ...(filters.earlyAdopter.length > 0 ? { earlyAdopter: filters.earlyAdopter.map((value) => (value ? 'Yes' : 'No')) } : {}),
  ...(filters.versions.length > 0 ? { versions: filters.versions } : {}),
});

const fromSharedState = (state: FilterState): TermsFilterState => ({
  pid: state.pid?.[0] ?? '',
  email: state.email?.[0] ?? '',
  earlyAdopter: (state.earlyAdopter ?? []).map((value) => value === 'Yes'),
  versions: state.versions ?? [],
});

export const applyTermsFilters = (rows: TermsRow[], filters: TermsFilterState): TermsRow[] => applyFilters(rows, TERMS_FILTER_CONFIGS, toSharedState(filters));

interface TermsFilterBarProps {
  filters: TermsFilterState;
  onChange: (filters: TermsFilterState) => void;
  /** multi-choice options derived from the loaded data (distinct versions, sorted) */
  versionOptions: string[];
}

export const TermsFilterBar = ({ filters, onChange, versionOptions }: TermsFilterBarProps) => {
  const configs = useMemo<Array<FilterConfig<TermsRow>>>(
    () => TERMS_FILTER_CONFIGS.map((config) => (config.key === 'versions' ? { ...config, options: versionOptions } : config)),
    [versionOptions],
  );

  return <TableFilters configs={configs} onChange={(next) => onChange(fromSharedState(next))} rows={NO_ROWS} state={toSharedState(filters)} />;
};
TermsFilterBar.displayName = 'TermsFilterBar';
